import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Avatar from 'react-avatar';
import { Tooltip } from "@mui/material";
import { VALIDATION_FIELD_MAPPING } from '../../../../../utils/appConstant';
import ElipsisText from "../../../../../components/ElipsisText";
import ElipsisCell from "../../../../../components/TableBox/Cells/EllipsisCell";
import Link from "../../../../../components/Link";
import Badge from "../../../../../components/Badge";
import Button from "../../../../../components/Button";
import { RowClassName, Modules, AuthRoutes, RouteParamsTypes, PermissionModulesEnum, PermissionActionsEnum } from "../../../../../enums";
import { TableContext } from "../../../../../contexts/TableContext";
import { AuthContext } from '../../../../../contexts/AuthContext';
import { hasResourseAccess, formatPhone } from '../../../../../utils/methods';

interface RowProps {
  item: any;
  className?: RowClassName;
}

const Row: React.FC<RowProps> = ({ item, className }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { headCells } = useContext(TableContext);
  const { isSystemAdmin, permissions } = useContext(AuthContext);

  const hasEditAccess = hasResourseAccess(isSystemAdmin, permissions, { accessibleTo: [{ [PermissionModulesEnum.Vendor]: [PermissionActionsEnum.Update] }] })

  const handleEditClick = (id: string) => {
    navigate(`${Modules.resources}${AuthRoutes.vendors}${RouteParamsTypes.edit}/${id}`);
  }

  const handleViewClick = (id: string) => {
    navigate(`${Modules.resources}${AuthRoutes.vendors}${RouteParamsTypes.view}/${id}`);
  }

  const nameCompanyNode = (
    <div className="d-flex align-items-center">
      <div className="me-2" style={{ width: 42 }}>
        <Avatar name={item.name || '-'} size="40" maxInitials={2} round />
      </div>
      <div style={{ width: 'calc(100% - 52px)' }}>
        <ElipsisText
          width="100%"
          text={item.name || '-'}
          node={
            <Link
              text={item.name || '-'}
              href={`${Modules.resources}${AuthRoutes.vendors}${RouteParamsTypes.view}/${item.id}`}
              onClick={() => handleViewClick(item.id)}
            />
          }
          className="h6 mb-1 text-decoration-underline hyperlink"
        />
        <ElipsisText width="100%" text={item.company_name || '-'} />
      </div>
    </div>
  );

  return (
    <tr className={className}>
      {headCells.map((headCell) => {
        if (headCell.id === VALIDATION_FIELD_MAPPING.name) {
          return (
            <ElipsisCell key={headCell.id} width={headCell.width} node={nameCompanyNode} />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.phoneNumber) {
          return (
            <ElipsisCell key={headCell.id} width={headCell.width} text={item?.phone ? formatPhone(item?.country_code, item?.phone) : '-'} />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.cities) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              text={item.cities && item.cities.length ? item.cities.map((item: any) => item.name).join(', ') : '-'}
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.serviceType) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              text={item?.service_type?.name ?? '-'}
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.status) {
          return (
            <td key={headCell.id} width={headCell.width}>
              <Badge
                text={item.is_active ? t("vendor.list.pageTexts.table.status.active") : t("vendor.list.pageTexts.table.status.inActive")}
                variant={item.is_active ? 'success' : 'danger'}
              />
            </td>
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.actions) {
          return (
            <td key={headCell.id} width={headCell.width}>
              <div className="d-flex align-items-sm-center justify-content-sm-start">
                {
                  hasEditAccess
                    ?
                    <Button
                      className="btn btn-sm btn-icon btn-text-secondary waves-effect waves-light rounded-pill"
                      icon={<i className="ri-edit-box-line ri-20px"></i>}
                      onClick={() => handleEditClick(item.id)}
                    />
                    :
                    <Button
                      className="btn btn-sm btn-icon btn-text-secondary waves-effect waves-light rounded-pill"
                      icon={<i className="ri-eye-line ri-20px"></i>}
                      onClick={() => handleViewClick(item.id)}
                    />
                }
                <Tooltip
                  title={
                    <>
                      <div>{t('vendor.list.pageTexts.table.lastUpdatedBy')} {item.updater?.name || item.updated_by || "-"}</div>
                      <div>{t('vendor.list.pageTexts.table.lastUpdated')} {item.updated_at || "-"}</div>
                    </>
                  }
                  arrow
                  classes={{
                    tooltip: 'tooltip',
                  }}
                >
                  <i className="ri-timer-line ri-20px"></i>
                </Tooltip>
              </div>
            </td>
          );
        }

        return (<ElipsisCell key={headCell.id} width={headCell.width} text={item[headCell.id] || '-'} />
        );
      })}
    </tr>
  );
};

export default Row;
